import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Account → Connections dialogs (Prototype v15 "Connections dialog"): one
 * shell for connecting an agent, adding or configuring a source, connecting a
 * WordPress site and making an API key. A title and a line under it, the body,
 * and a footer with ← Back (on later steps) and the one thing to do next.
 */
export function ConnDialog({
  title,
  sub,
  onClose,
  onBack,
  cta,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  cta: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
  };
  children?: React.ReactNode;
}) {
  const titleId = useId();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    card.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div className="cx-scrim" onClick={onClose} />
      <div
        className="cx-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={card}
      >
        <div className="cx-head">
          <div className="cx-head-text">
            <div className="cx-title" id={titleId}>
              {title}
            </div>
            {sub ? <div className="cx-sub">{sub}</div> : null}
          </div>
          <button type="button" className="cx-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {children ? <div className="cx-body">{children}</div> : null}
        <div className="cx-foot">
          {onBack ? (
            <button type="button" className="cx-back" onClick={onBack}>
              ← Back
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-primary cx-cta"
            data-danger={cta.danger ? 'true' : undefined}
            disabled={cta.disabled}
            onClick={cta.onClick}
          >
            {cta.label}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

/** "What are you connecting?": one choice of a few, each with a line under it. */
export function RadioCards<T extends string>({
  label,
  options,
  value,
  onChange,
  columns,
}: {
  label?: string;
  options: Array<{ value: T; title: string; summary: string }>;
  value: T | null;
  onChange: (v: T) => void;
  /** Two cards a row, for a long list (Connect an agent). */
  columns?: 2;
}) {
  return (
    <div className="cx-field" role="radiogroup" aria-label={label} data-cols={columns}>
      {label ? <span className="cx-label">{label}</span> : null}
      {options.map((o) => (
        <button
          type="button"
          role="radio"
          aria-checked={value === o.value}
          key={o.value}
          className="cx-radio"
          data-on={value === o.value ? 'true' : undefined}
          onClick={() => onChange(o.value)}
        >
          <span className="cx-radio-dot" />
          <span className="cx-radio-text">
            <span className="cx-radio-title">{o.title}</span>
            <span className="cx-radio-summary">{o.summary}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** A labelled text field with its note (red when it's wrong). */
export function CxInput({
  label,
  value,
  onChange,
  placeholder,
  note,
  bad,
  mono,
  maxLength = 60,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  note?: string;
  bad?: boolean;
  mono?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
}) {
  return (
    <label className="cx-field">
      <span className="cx-label">{label}</span>
      <input
        className="cx-input"
        data-bad={bad ? 'true' : undefined}
        data-mono={mono ? 'true' : undefined}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
      {note ? (
        <span className="cx-note" data-bad={bad ? 'true' : undefined}>
          {note}
        </span>
      ) : null}
    </label>
  );
}

/** A value shown once, with Copy beside it. */
export function CxCopyRow({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: 'copied' | 'failed' | null;
  onCopy: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <div className="cx-field">
      <span className="cx-label">{label}</span>
      <div className="cx-copy-row">
        <code className="cx-value">{value}</code>
        <button type="button" className="ink-btn" onClick={onCopy}>
          {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/** Numbered steps; a step may carry a line of code to copy by hand. */
export function CxSteps({
  head,
  steps,
}: {
  head: string;
  steps: Array<{ t: string; code?: string }>;
}) {
  return (
    <div>
      <div className="cx-steps-head">{head}</div>
      <ol className="cx-steps">
        {steps.map((s, i) => (
          <li key={i}>
            <span className="cx-step-n">{i + 1}</span>
            <div>
              <div>{s.t}</div>
              {s.code ? <code className="cx-step-code">{s.code}</code> : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
